import { useState, type ReactNode } from "react";
import { Box, ButtonBase, Collapse, IconButton, InputBase, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import AlternateEmail from "@mui/icons-material/AlternateEmail";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import CallOutlined from "@mui/icons-material/CallOutlined";
import ChairOutlined from "@mui/icons-material/ChairOutlined";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FacebookOutlined from "@mui/icons-material/FacebookOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import Instagram from "@mui/icons-material/Instagram";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import MeetingRoomOutlined from "@mui/icons-material/MeetingRoomOutlined";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import PhotoLibraryOutlined from "@mui/icons-material/PhotoLibraryOutlined";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import SensorsOutlined from "@mui/icons-material/SensorsOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import StarOutline from "@mui/icons-material/StarOutline";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import ArchitectureOutlined from "@mui/icons-material/ArchitectureOutlined";
import BatteryChargingFullOutlined from "@mui/icons-material/BatteryChargingFullOutlined";
import Close from "@mui/icons-material/Close";
import ElectricalServicesOutlined from "@mui/icons-material/ElectricalServicesOutlined";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import PowerOutlined from "@mui/icons-material/PowerOutlined";
import RouterOutlined from "@mui/icons-material/RouterOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import SignpostOutlined from "@mui/icons-material/SignpostOutlined";
import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";

/** Who can see a venue detail on the website (backend: venue/utils.py). */
export type Audience = "everyone" | "members" | "hidden";

/** One detail the website can show, as the backend describes it. */
export interface VisibilityField {
  key: string;
  label: string;
  group: string;
  group_label: string;
  default: Audience;
  locked: boolean;
  /** Extra fields stay off the settings until an Admin adds them. */
  optional?: boolean;
  /** For optional fields: whether an Admin has added it. */
  added?: boolean;
  /** For optional fields: the part of the venue page it joins ("layouts", "visiting", "location", "streams", "amenities"). */
  shows_in?: string;
  /** Who sees it on the website; set once for every venue. */
  audience: Audience;
}

export const AUDIENCES: Audience[] = ["everyone", "members", "hidden"];

export const AUDIENCE_META: Record<Audience, { label: string; short: string; hint: string; icon: ReactNode }> = {
  everyone: { label: "Everyone", short: "Everyone", hint: "Anyone browsing muzukuru.com", icon: <PublicOutlined /> },
  members: { label: "Members only", short: "Members", hint: "Visitors who sign in with a free account", icon: <LockOutlined /> },
  hidden: { label: "Hidden", short: "Hidden", hint: "Kept in MMS, never on the website", icon: <VisibilityOffOutlined /> },
};

export const GROUP_ICONS: Record<string, ReactNode> = {
  listing: <StorefrontOutlined />,
  location: <LocationOnOutlined />,
  contact: <AccountCircleOutlined />,
  online: <ShareOutlined />,
  space: <ChairOutlined />,
  livestreaming: <SensorsOutlined />,
  community: <StarOutline />,
};

export const FIELD_ICONS: Record<string, ReactNode> = {
  identity: <StorefrontOutlined />,
  cover: <ImageOutlined />,
  gallery: <PhotoLibraryOutlined />,
  description: <NotesOutlined />,
  capacity: <GroupsOutlined />,
  street_address: <LocationOnOutlined />,
  map: <MapOutlined />,
  contact_name: <BadgeOutlined />,
  contact_photo: <PersonOutline />,
  contact_phone: <CallOutlined />,
  contact_email: <AlternateEmail />,
  website: <LanguageOutlined />,
  facebook: <FacebookOutlined />,
  instagram: <Instagram />,
  amenities: <ChecklistOutlined />,
  layouts: <ChairOutlined />,
  rooms: <MeetingRoomOutlined />,
  streams: <PlayCircleOutline />,
  reviews: <StarOutline />,
  floor_plans: <ArchitectureOutlined />,
  location_notes: <SignpostOutlined />,
  country: <FlagOutlined />,
  time_zone: <ScheduleOutlined />,
  has_power: <PowerOutlined />,
  power_type: <ElectricalServicesOutlined />,
  backup_power_capacity: <BatteryChargingFullOutlined />,
  internet_provider: <RouterOutlined />,
  upload_speed: <SpeedOutlined />,
  number_of_cameras: <VideocamOutlined />,
};

const field = (key: string, label: string, group: string, groupLabel: string, audience: Audience, locked = false): VisibilityField => ({
  key, label, group, group_label: groupLabel, default: audience, locked, audience,
});

/** The backend's defaults, used only if the server doesn't send the fields (an older API). */
export const DEFAULT_FIELDS: VisibilityField[] = [
  field("identity", "Name, type and area", "listing", "The listing", "everyone", true),
  field("cover", "Cover photo", "listing", "The listing", "everyone", true),
  field("gallery", "Photo gallery", "listing", "The listing", "everyone"),
  field("description", "Description", "listing", "The listing", "everyone"),
  field("capacity", "Capacity", "listing", "The listing", "everyone"),
  field("street_address", "Street address", "location", "Location", "members"),
  field("map", "Map pin and directions", "location", "Location", "members"),
  field("contact_name", "Contact's name", "contact", "Contact", "members"),
  field("contact_photo", "Contact's photo", "contact", "Contact", "members"),
  field("contact_phone", "Phone number", "contact", "Contact", "members"),
  field("contact_email", "Email address", "contact", "Contact", "members"),
  field("website", "Website", "online", "Online", "members"),
  field("facebook", "Facebook", "online", "Online", "members"),
  field("instagram", "Instagram", "online", "Online", "members"),
  field("amenities", "Amenities", "space", "The space", "members"),
  field("layouts", "Layouts", "space", "The space", "members"),
  field("rooms", "Rooms", "space", "The space", "hidden"),
  field("streams", "Past streams", "livestreaming", "Livestreaming", "members"),
  field("reviews", "Reviews and rating", "community", "Reviews", "everyone"),
];

export const WEBSITE_SETTINGS_PATH = "/configurations/venue-website-visibility";

/** The venue page's sections in their default order (backend: PAGE_SECTIONS), for an API that doesn't send them. */
export const DEFAULT_SECTIONS = [
  { key: "about", label: "About" },
  { key: "amenities", label: "Amenities" },
  { key: "layouts", label: "Layouts" },
  { key: "rooms", label: "Rooms" },
  { key: "streams", label: "Past streams" },
  { key: "reviews", label: "Reviews" },
];

/** Fields grouped in the backend's order. */
export function groupFields(fields: VisibilityField[]) {
  const groups: { key: string; label: string; fields: VisibilityField[] }[] = [];
  for (const item of fields) {
    const group = groups.find((existing) => existing.key === item.group);
    if (group) group.fields.push(item);
    else groups.push({ key: item.group, label: item.group_label, fields: [item] });
  }
  return groups;
}

/** The one audience every changeable field in a group shares, or null when they differ. */
export function commonAudience(fields: VisibilityField[], audienceOf: (field: VisibilityField) => Audience): Audience | null {
  const values = new Set(fields.filter((item) => !item.locked).map(audienceOf));
  return values.size === 1 ? [...values][0] : null;
}

/**
 * What signing in unlocks, for the sign-in card: a group by name when all of it is
 * for members, else its fields (backend: members_only_labels).
 */
export function unlockList(fields: VisibilityField[], audienceOf: (field: VisibilityField) => Audience) {
  return groupFields(fields).flatMap((group) => {
    const changeable = group.fields.filter((field) => !field.locked && !(field.optional && audienceOf(field) === "hidden"));
    const locked = changeable.filter((field) => audienceOf(field) === "members");
    return locked.length > 1 && locked.length === changeable.length ? [group.label] : locked.map((field) => field.label);
  });
}

const RANK: Record<Audience, number> = { hidden: 0, members: 1, everyone: 2 };
const NEEDS: { key: string; needs: string; hint: string }[] = [
  { key: "contact_photo", needs: "contact_name", hint: "Some visitors will see the photo without a name." },
  { key: "map", needs: "street_address", hint: "Directions still lead to the venue, even where the street address is hidden." },
];

/** A gentle warning for combinations that look odd to some visitors. */
export function hintFor(key: string, audienceOf: (key: string) => Audience) {
  const rule = NEEDS.find((item) => item.key === key);
  return rule && RANK[audienceOf(key)] > RANK[audienceOf(rule.needs)] ? rule.hint : undefined;
}

// Fixed colours, so the meaning stays the same whatever the MMS theme colour is.
const TONES: Record<Audience, string> = { everyone: "#16A34A", members: "#2563EB", hidden: "#64748B" };

export function toneColor(_theme: Theme, audience: Audience) {
  return TONES[audience];
}

/**
 * Everyone / Members / Hidden as a segmented control: equal segments on a soft
 * track, the chosen one raised in white. `null` means the fields underneath
 * differ, so nothing is chosen.
 */
export function AudienceSegment({
  label,
  value,
  disabled,
  onChange,
  size = "medium",
}: {
  label: string;
  value: Audience | null;
  disabled?: boolean;
  onChange: (audience: Audience) => void;
  /** "small" for single fields, so a group's own switch stands out above them. */
  size?: "medium" | "small";
}) {
  const theme = useTheme();
  const small = size === "small";
  return (
    <Tooltip title={value === null ? "Set differently inside. Pick one to set them all." : ""} placement="top">
      <Box
        role="radiogroup"
        aria-label={`Who sees ${label}`}
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "inline-grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0.25,
          p: small ? 0.25 : 0.375,
          borderRadius: small ? 2 : 2.5,
          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,.06)" : "#F1F3F7",
          border: 1,
          borderColor: value === null ? alpha(theme.palette.warning.main, 0.5) : "transparent",
          borderStyle: value === null ? "dashed" : "solid",
          flexShrink: 0,
        }}
      >
        {AUDIENCES.map((audience) => {
          const selected = value === audience;
          const color = toneColor(theme, audience);
          return (
            <ButtonBase
              key={audience}
              role="radio"
              aria-checked={selected}
              aria-label={AUDIENCE_META[audience].label}
              disabled={disabled}
              onClick={() => onChange(audience)}
              sx={{
                minWidth: small ? 72 : 88,
                height: small ? 24 : 30,
                px: small ? 0.9 : 1.25,
                gap: small ? 0.4 : 0.6,
                borderRadius: small ? 1.5 : 2,
                fontSize: small ? 11.5 : 12.5,
                fontWeight: selected ? 700 : 600,
                color: selected ? color : "text.secondary",
                bgcolor: selected ? "background.paper" : "transparent",
                boxShadow: selected ? "0 1px 2px rgba(16,24,40,.08), 0 1px 3px rgba(16,24,40,.10)" : "none",
                transition: "background-color .15s, color .15s, box-shadow .15s",
                "& svg": { fontSize: small ? 14 : 16, color: selected ? color : "inherit", opacity: selected ? 1 : 0.8 },
                "&:hover": selected ? {} : { color: "text.primary", bgcolor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.6) },
                "&.Mui-disabled": { opacity: selected ? 0.85 : 0.5 },
                "&.Mui-focusVisible": { outline: `2px solid ${color}`, outlineOffset: 1 },
              }}
            >
              {AUDIENCE_META[audience].icon}
              {AUDIENCE_META[audience].short}
            </ButtonBase>
          );
        })}
      </Box>
    </Tooltip>
  );
}

/** One coloured dot per field, so a closed group still shows who sees what. */
function AudienceDots({ fields, audienceOf }: { fields: VisibilityField[]; audienceOf: (field: VisibilityField) => Audience }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" aria-hidden>
      {fields.map((item) => (
        <Box key={item.key} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: toneColor(theme, audienceOf(item)) }} />
      ))}
    </Stack>
  );
}

/** A group of fields that opens to show each one. Its own control usually sets them all. */
export function VisibilityGroup({
  groupKey,
  label,
  fields,
  audienceOf,
  control,
  renderField,
  defaultOpen = false,
  flag,
  onRename,
  defaultLabel,
}: {
  groupKey: string;
  label: string;
  fields: VisibilityField[];
  audienceOf: (field: VisibilityField) => Audience;
  control: ReactNode;
  renderField: (field: VisibilityField) => { control: ReactNode; value?: ReactNode; hint?: string; missing?: boolean; onRemove?: () => void };
  defaultOpen?: boolean;
  /** A small marker after the name. */
  flag?: ReactNode;
  /** Lets Admins rename the group; a blank name goes back to `defaultLabel`. */
  onRename?: (name: string) => void;
  defaultLabel?: string;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState<string | null>(null);
  const commit = () => {
    if (editing !== null) onRename?.(editing.trim());
    setEditing(null);
  };
  const common = commonAudience(fields, audienceOf);
  const tint = toneColor(theme, common ?? "members");
  return (
    <Box sx={{ borderRadius: 3, border: 1, borderColor: open ? alpha(theme.palette.primary.main, 0.3) : "divider", bgcolor: "background.paper", overflow: "hidden", transition: "border-color .2s, box-shadow .2s", boxShadow: open ? "0 6px 20px rgba(17,24,39,.06)" : "none" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${label} details`}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
        sx={{ display: "flex", flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center", columnGap: 1.5, rowGap: 1, px: 2, py: 1.5, cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 } }}
      >
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", flexShrink: 0, bgcolor: alpha(tint, 0.12), color: common ? tint : "text.secondary", transition: "all .2s", "& svg": { fontSize: 21 } }}>
          {GROUP_ICONS[groupKey]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing !== null ? (
            <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <InputBase
                autoFocus
                value={editing}
                onChange={(event) => setEditing(event.target.value.slice(0, 40))}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") commit();
                  if (event.key === "Escape") setEditing(null);
                }}
                placeholder={defaultLabel}
                inputProps={{ "aria-label": `Name for ${label}` }}
                sx={{ fontWeight: 800, fontSize: 16, px: 1, py: 0.1, ml: -1, borderRadius: 1.5, bgcolor: "action.hover", width: "100%", maxWidth: 260 }}
              />
            </Box>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ "&:hover .rename, & .rename:focus-visible": { opacity: 1 } }}>
              <Typography fontWeight={800} noWrap>{label}</Typography>
              {onRename && (
                <Tooltip title="Rename">
                  <IconButton
                    className="rename"
                    size="small"
                    aria-label={`Rename ${label}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditing(label);
                    }}
                    sx={{ p: 0.25, opacity: 0.35, transition: "opacity .15s" }}
                  >
                    <EditOutlined sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {flag}
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <AudienceDots fields={fields} audienceOf={audienceOf} />
            <Typography variant="caption" color="text.secondary">
              {fields.length}
              {common === null && " · mixed"}
            </Typography>
          </Stack>
        </Box>
        <ExpandMore sx={{ order: { sm: 3 }, color: "text.secondary", transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }} />
        {/* On phones the control drops onto its own row under the name. */}
        <Box sx={{ order: { xs: 4, sm: 2 }, width: { xs: "100%", sm: "auto" }, pl: { xs: 6.5, sm: 0 } }}>{control}</Box>
      </Box>

      <Collapse in={open}>
        <Box sx={{ borderTop: 1, borderColor: "divider", py: 0.5 }}>
          {fields.map((item) => {
            const row = renderField(item);
            return (
              <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: { xs: 2, sm: 3 }, pr: 2, py: 1.1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <Box sx={{ color: row.missing ? "warning.main" : "text.secondary", display: "flex", "& svg": { fontSize: 19 } }}>{FIELD_ICONS[item.key]}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                    {row.hint && (
                      <Tooltip title={row.hint}>
                        <WarningAmberRounded aria-label={row.hint} sx={{ fontSize: 16, color: "warning.main" }} />
                      </Tooltip>
                    )}
                  </Stack>
                  {row.value && (
                    <Typography component="div" variant="caption" color={row.missing ? "warning.dark" : "text.secondary"} sx={{ display: "block", overflowWrap: "anywhere", lineHeight: 1.5 }}>
                      {row.value}
                    </Typography>
                  )}
                </Box>
                {item.locked ? (
                  <Tooltip title="Every listing needs this">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.disabled", fontSize: 12, fontWeight: 700 }}>
                      <LockOutlined sx={{ fontSize: 15 }} />
                      <span>Always shown</span>
                    </Stack>
                  </Tooltip>
                ) : (
                  row.control
                )}
                {/* Every row keeps this slot, so the switches line up with the group's. */}
                <Box sx={{ width: 24, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                  {row.onRemove && (
                    <Tooltip title="Remove from the website settings">
                      <IconButton size="small" aria-label={`Remove ${item.label}`} onClick={row.onRemove} sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "error.main" } }}>
                        <Close sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
